import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { admin as adminApi, setToken } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';

const ROLE_LABELS = {
  admin: 'Admin',
  coordinator: 'Coordinateur',
  driver: 'Livreur',
  partner: 'Partenaire',
};

export default function UsersPage() {
  const { user, loadUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyUserId, setBusyUserId] = useState('');

  useEffect(() => {
    if (user && user.globalRole !== 'superadmin') {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.globalRole !== 'superadmin') return;
    let cancelled = false;
    setLoading(true);
    adminApi.listUsers()
      .then((data) => { if (!cancelled) setUsers(data); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Erreur chargement utilisateurs'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.globalRole]);

  const rows = useMemo(() => users.filter((u) => u.id !== user?.id), [users, user?.id]);

  const handleViewAs = async (target) => {
    const reason = window.prompt(`Raison RGPD pour voir comme ${target.email} ?`);
    if (!reason || !reason.trim()) return;
    setBusyUserId(target.id);
    setError('');
    try {
      const out = await adminApi.impersonate(target.id, reason.trim());
      setToken(out.token);
      await loadUser();
      navigate('/cee/dossiers', { replace: true });
    } catch (err) {
      setError(err.message || 'Impersonation impossible');
    } finally {
      setBusyUserId('');
    }
  };

  if (user?.globalRole !== 'superadmin') return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <header className="mb-5">
        <h1 className="text-2xl font-bold text-[#0f172a]">Utilisateurs</h1>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10">
          <div className="w-7 h-7 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto" />
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left p-3 font-semibold text-[#475569]">Email</th>
                <th className="text-left p-3 font-semibold text-[#475569]">Nom</th>
                <th className="text-left p-3 font-semibold text-[#475569]">Role</th>
                <th className="text-left p-3 font-semibold text-[#475569]">Mandataire</th>
                <th className="text-left p-3 font-semibold text-[#475569]">Statut</th>
                <th className="p-3 text-right font-semibold text-[#475569]">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={`${u.id}-${u.campaignId || 'global'}`} className="border-b last:border-0">
                  <td className="p-3 font-medium text-[#0f172a]">{u.email}</td>
                  <td className="p-3 text-[#475569]">{u.name}</td>
                  <td className="p-3">
                    <span className="px-2 py-1 rounded-md bg-gray-100 text-xs font-medium text-[#334155]">
                      {ROLE_LABELS[u.role] || u.globalRole || '-'}
                    </span>
                  </td>
                  <td className="p-3 text-[#475569]">{u.mandataireCode || '-'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                      u.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {u.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleViewAs(u)}
                      disabled={!u.isActive || busyUserId === u.id}
                      className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {busyUserId === u.id ? 'Connexion...' : 'Voir comme'}
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-[#94a3b8]">Aucun utilisateur</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
